import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Home, TrendingUp, MapPin, Users, DollarSign, Clock, Search, Star, CheckCircle, ArrowRight, Building, School, Car, History, Lightbulb, Heart, BookOpen, Landmark } from "lucide-react";
import { Helmet } from "react-helmet";
import axios from "axios";
import { dfwCities, dfwCounties } from "@/data/dfwGeoData";
import { getCityHistory } from "@/data/cityHistoryData";
import { getCityImage } from "@/data/dfwHomeImages";
import SiteNavigation from "@/components/SiteNavigation";

const API = process.env.REACT_APP_BACKEND_URL;

const CityLandingEnhanced = ({ user }) => {
  const { citySlug } = useParams();
  const navigate = useNavigate();
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalListings, setTotalListings] = useState(0);

  const city = dfwCities[citySlug];
  const cityHistory = getCityHistory(citySlug);
  const cityImage = getCityImage(citySlug);

  // All hooks MUST be called before any conditional returns
  useEffect(() => {
    if (city) {
      fetchListings();
    }
  }, [citySlug, city]);

  const fetchListings = async () => {
    if (!city) return;
    try {
      setLoading(true);
      const response = await axios.get(`${API}/api/listings`, {
        params: { city: city.name, limit: 12, status: "active" }
      });
      const data = Array.isArray(response.data) ? response.data : [];
      setListings(data);
      setTotalListings(data.length);
    } catch (error) {
      console.error("Error fetching listings:", error);
      setListings([]);
    } finally {
      setLoading(false);
    }
  };
  
  // If city not found, show 404-like message (AFTER all hooks)
  if (!city) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 to-amber-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">City Not Found</h1>
          <p className="text-gray-600 mb-6">We couldn&apos;t find FSBO listings for this city.</p>
          <Button onClick={() => navigate('/search')}>Browse All Listings</Button>
        </div>
      </div>
    );
  }

  // Generate structured data for SEO
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": `${city.name} Homes For Sale By Owner | FSBO ${city.name} TX`,
    "description": `Find ${city.name}, Texas homes for sale by owner. ${cityHistory ? `Founded in ${cityHistory.founded}. ` : ''}Browse FSBO listings in ${city.county} County. Save thousands on agent fees selling your ${city.name} home yourself.`,
    "url": `https://its-sold.com/homes-for-sale/${citySlug}`,
    "about": cityHistory ? {
      "@type": "City",
      "name": city.name,
      "description": city.description,
      "foundingDate": cityHistory.founded,
      "containedInPlace": {
        "@type": "AdministrativeArea",
        "name": `${city.county} County, Texas, USA`
      }
    } : undefined,
    "mainEntity": {
      "@type": "ItemList",
      "name": `FSBO Homes in ${city.name}, TX`,
      "description": `For Sale By Owner real estate listings in ${city.name}, ${city.county} County, Texas`,
      "numberOfItems": totalListings,
      "itemListElement": listings.slice(0, 10).map((listing, index) => ({
        "@type": "ListItem",
        "position": index + 1,
        "item": {
          "@type": "House",
          "name": listing.title,
          "description": listing.description?.substring(0, 200),
          "address": {
            "@type": "PostalAddress",
            "streetAddress": listing.address,
            "addressLocality": listing.city,
            "addressRegion": "TX",
            "postalCode": listing.zip_code,
            "addressCountry": "US"
          },
          "numberOfRooms": listing.bedrooms,
          "numberOfBathroomsTotal": listing.bathrooms,
          "floorSize": {
            "@type": "QuantitativeValue",
            "value": listing.sqft,
            "unitCode": "FTK"
          }
        }
      }))
    },
    "breadcrumb": {
      "@type": "BreadcrumbList",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://its-sold.com" },
        { "@type": "ListItem", "position": 2, "name": "Texas", "item": "https://its-sold.com/homes-for-sale" },
        { "@type": "ListItem", "position": 3, "name": city.county, "item": `https://its-sold.com/county/${city.county.toLowerCase().replace(' ', '-')}-county` },
        { "@type": "ListItem", "position": 4, "name": city.name, "item": `https://its-sold.com/homes-for-sale/${citySlug}` }
      ]
    },
    "publisher": {
      "@type": "Organization",
      "name": "Its-Sold.com",
      "url": "https://its-sold.com",
      "logo": "https://its-sold.com/logo.png"
    }
  };

  const localBusinessData = {
    "@context": "https://schema.org",
    "@type": "RealEstateAgent",
    "name": `Its-Sold.com - ${city.name} FSBO`,
    "description": `Free For Sale By Owner real estate listings in ${city.name}, Texas. List your home for free and save thousands on agent commissions.`,
    "url": `https://its-sold.com/homes-for-sale/${citySlug}`,
    "telephone": "+1-877-394-0210",
    "areaServed": {
      "@type": "City",
      "name": city.name,
      "containedInPlace": {
        "@type": "AdministrativeArea",
        "name": city.county + " County, Texas"
      }
    },
    "priceRange": "Free",
    "address": {
      "@type": "PostalAddress",
      "addressLocality": city.name,
      "addressRegion": "TX",
      "addressCountry": "US"
    }
  };

  const faqData = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": `How do I sell my house by owner in ${city.name}, TX?`,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": `To sell your ${city.name} home by owner (FSBO), list it for FREE on Its-Sold.com. Create your listing with photos, set your price, and connect directly with buyers. You'll save 6% in agent commissions - that's $${Math.round(parseInt(city.medianPrice.replace(/[$,]/g, '')) * 0.06).toLocaleString()} on the median ${city.name} home!`
        }
      },
      {
        "@type": "Question", 
        "name": `What is the median home price in ${city.name}?`,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": `The median home price in ${city.name}, TX is approximately ${city.medianPrice}. This varies by neighborhood - areas like ${city.neighborhoods.slice(0, 2).join(' and ')} may be higher, while other areas offer more affordable options.`
        }
      },
      {
        "@type": "Question",
        "name": `How much can I save selling FSBO in ${city.name}?`,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": `Selling FSBO in ${city.name} typically saves homeowners 5-6% in real estate commissions. On the median ${city.name} home price of ${city.medianPrice}, that's approximately $${Math.round(parseInt(city.medianPrice.replace(/[$,]/g, '')) * 0.055).toLocaleString()} in savings!`
        }
      }
    ]
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 to-amber-50">
      {/* SEO Meta Tags */}
      <Helmet>
        <title>{city.name} Homes For Sale By Owner | FSBO {city.name} TX | Its-Sold.com™</title>
        <meta name="description" content={`${city.name}, TX homes for sale by owner (FSBO). ${cityHistory ? `Founded ${cityHistory.founded}, ${city.name} offers ${cityHistory.whyLiveHere?.[0]?.toLowerCase() || 'excellent living'}. ` : ''}Browse ${city.name} real estate in ${city.county} County. FREE listings - save thousands!`} />
        <meta name="keywords" content={city.keywords} />
        <link rel="canonical" href={`https://its-sold.com/homes-for-sale/${citySlug}`} />
        
        {/* Open Graph */}
        <meta property="og:title" content={`${city.name} FSBO Homes | For Sale By Owner | Its-Sold.com`} />
        <meta property="og:description" content={`Browse homes for sale by owner in ${city.name}, TX. No agent fees! List FREE on Its-Sold.com.`} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`https://its-sold.com/homes-for-sale/${citySlug}`} />
        <meta property="og:image" content={cityImage.url} />
        <meta property="og:image:alt" content={cityImage.alt} />
        <meta property="og:site_name" content="Its-Sold.com" />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`${city.name} Homes For Sale By Owner`} />
        <meta name="twitter:description" content={`FSBO listings in ${city.name}, TX. Save thousands - list FREE!`} />
        <meta name="twitter:image" content={cityImage.url} />
        <meta name="twitter:image:alt" content={cityImage.alt} />
        
        {/* Geo Tags */}
        <meta name="geo.region" content="US-TX" />
        <meta name="geo.placename" content={`${city.name}, Texas`} />
        
        {/* Schema.org Structured Data */}
        <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
        <script type="application/ld+json">{JSON.stringify(localBusinessData)}</script>
        <script type="application/ld+json">{JSON.stringify(faqData)}</script>
      </Helmet>

      {/* Consistent Navigation */}
      <SiteNavigation />

      {/* Breadcrumb */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <nav className="text-sm text-gray-600 overflow-x-auto">
            <ol className="flex items-center space-x-2 whitespace-nowrap">
              <li><Link to="/" className="hover:text-orange-600">Home</Link></li>
              <li>/</li>
              <li><Link to="/search" className="hover:text-orange-600">TX</Link></li>
              <li>/</li>
              <li className="hidden sm:inline"><Link to={`/county/${city.county.toLowerCase().replace(' ', '-')}-county`} className="hover:text-orange-600">{city.county}</Link></li>
              <li className="hidden sm:inline">/</li>
              <li className="text-orange-600 font-medium">{city.name}</li>
            </ol>
          </nav>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Hero Section with Featured Image */}
        <header className="mb-12">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div className="text-center md:text-left">
              <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                {city.name} Homes For Sale By Owner
              </h1>
              <p className="text-xl text-gray-700 mb-2">
                FSBO Listings in {city.name}, {city.county} County, TX
              </p>
              <p className="text-base text-gray-600 mb-6">
                {city.description}
              </p>
              
              {/* Quick Stats Row */}
              <div className="flex flex-wrap justify-center md:justify-start gap-4 text-sm mb-6">
                <div className="flex items-center">
                  <Users className="w-4 h-4 text-orange-600 mr-1" />
                  <span><strong>Population:</strong> {city.population}</span>
                </div>
                <div className="flex items-center">
                  <DollarSign className="w-4 h-4 text-green-600 mr-1" />
                  <span><strong>Median Price:</strong> {city.medianPrice}</span>
                </div>
                <div className="flex items-center">
                  <Car className="w-4 h-4 text-blue-600 mr-1" />
                  <span><strong>Commute:</strong> {city.commute}</span>
                </div>
              </div>

              <Button 
                onClick={() => navigate('/register')} 
                className="bg-orange-600 hover:bg-orange-700 text-white font-bold text-lg px-8 py-6"
              >
                List Your {city.name} Home FREE →
              </Button>
            </div>

            {/* Featured Home Image */}
            <div className="relative">
              <div className="rounded-xl overflow-hidden shadow-2xl">
                <img 
                  src={cityImage.url} 
                  alt={cityImage.alt}
                  title={cityImage.title}
                  className="w-full h-64 md:h-80 object-cover"
                  loading="eager"
                />
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-4">
                  <p className="text-white text-sm font-medium">
                    Featured: {cityImage.beds} Bedroom {cityImage.style.charAt(0).toUpperCase() + cityImage.style.slice(1)} Home in {city.name}
                  </p>
                </div>
              </div>
              <div className="absolute -top-3 -right-3 bg-orange-600 text-white px-4 py-2 rounded-full font-bold text-sm shadow-lg">
                FSBO Homes
              </div>
            </div>
          </div>
        </header>

        {/* Key Benefits Section */}
        <section className="bg-gradient-to-r from-orange-600 to-amber-500 rounded-xl p-6 md:p-8 text-white mb-12">
          <h2 className="text-2xl md:text-3xl font-bold mb-6 text-center">
            Why Sell FSBO in {city.name}?
          </h2>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="text-4xl font-bold mb-2">${Math.round(parseInt(city.medianPrice.replace(/[$,]/g, '')) * 0.06).toLocaleString()}</div>
              <p>Average Commission Saved</p>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold mb-2">$0</div>
              <p>Cost to List on Its-Sold.com</p>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold mb-2">100%</div>
              <p>Control Over Your Sale</p>
            </div>
          </div>
          <div className="text-center mt-6">
            <Button 
              onClick={() => navigate('/register')} 
              className="bg-white text-orange-600 hover:bg-gray-100 font-bold text-lg px-8 py-6"
            >
              List Your {city.name} Home FREE →
            </Button>
          </div>
        </section>

        {/* City History & Heritage Section */}
        {cityHistory && (
          <section className="mb-12 bg-white rounded-xl shadow-lg overflow-hidden">
            <div className="bg-gradient-to-r from-slate-800 to-slate-700 p-6">
              <h2 className="text-2xl md:text-3xl font-bold text-white flex items-center gap-3">
                <Landmark className="w-8 h-8" />
                {city.name} History & Heritage
              </h2>
              <p className="text-slate-200 mt-2">
                Founded in {cityHistory.founded} • {cityHistory.namedAfter}
              </p>
            </div>
            
            <div className="p-6 md:p-8 space-y-8">
              {/* Historical Highlights */}
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <History className="w-5 h-5 text-amber-600" />
                  Historical Timeline
                </h3>
                <div className="relative border-l-2 border-amber-400 pl-6 space-y-4 ml-2">
                  {cityHistory.historicalHighlights.map((highlight, index) => (
                    <div key={index} className="relative">
                      <div className="absolute -left-8 w-4 h-4 bg-amber-500 rounded-full border-2 border-white"></div>
                      <p className="text-gray-700">{highlight}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Fun Facts */}
              <div className="bg-amber-50 rounded-lg p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <Lightbulb className="w-5 h-5 text-amber-600" />
                  Fun Facts About {city.name}
                </h3>
                <div className="grid md:grid-cols-2 gap-4">
                  {cityHistory.funFacts.map((fact, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <Star className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                      <p className="text-gray-700">{fact}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Why Live Here */}
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <Heart className="w-5 h-5 text-red-500" />
                  Why Homebuyers Love {city.name}
                </h3>
                <div className="grid md:grid-cols-2 gap-4">
                  {cityHistory.whyLiveHere.map((reason, index) => (
                    <div key={index} className="flex items-start gap-3 bg-green-50 p-3 rounded-lg">
                      <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <p className="text-gray-700">{reason}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Buyer Tips */}
              <div className="bg-blue-50 rounded-lg p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <BookOpen className="w-5 h-5 text-blue-600" />
                  Insider Tips for Buying in {city.name}
                </h3>
                <ul className="space-y-3">
                  {cityHistory.buyerTips.map((tip, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <span className="bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center flex-shrink-0 text-sm font-bold">
                        {index + 1}
                      </span>
                      <p className="text-gray-700">{tip}</p>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Quick Info Grid */}
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div className="bg-gray-50 p-4 rounded-lg text-center">
                  <p className="text-sm text-gray-500 mb-1">Climate</p>
                  <p className="font-semibold text-gray-800">{cityHistory.climate}</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg text-center">
                  <p className="text-sm text-gray-500 mb-1">Major Industries</p>
                  <p className="font-semibold text-gray-800">{cityHistory.economy}</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg text-center col-span-2 md:col-span-1">
                  <p className="text-sm text-gray-500 mb-1">Notable Residents</p>
                  <p className="font-semibold text-gray-800">{cityHistory.famousResidents}</p>
                </div>
              </div>
            </div>
          </section>
        )}

        {/* Popular Neighborhoods */}
        <section className="mb-12">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6">
            Popular {city.name} Neighborhoods
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3">
            {city.neighborhoods.map((neighborhood) => (
              <Button
                key={neighborhood}
                variant="outline"
                className="border-2 border-orange-200 hover:bg-orange-50 hover:border-orange-400 text-left justify-start"
                onClick={() => navigate(`/search?city=${city.name}`)}
              >
                <MapPin className="w-4 h-4 mr-2 text-orange-600 flex-shrink-0" />
                <span className="truncate">{neighborhood}</span>
              </Button>
            ))}
          </div>
        </section>

        {/* School Districts */}
        {city.schoolDistricts && (
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              <School className="w-6 h-6 inline mr-2 text-blue-600" />
              {city.name} School Districts
            </h2>
            <div className="flex flex-wrap gap-3">
              {city.schoolDistricts.map((district) => (
                <span key={district} className="bg-blue-50 text-blue-700 px-4 py-2 rounded-full text-sm font-medium">
                  {district}
                </span>
              ))}
            </div>
          </section>
        )}

        {/* Featured Listings */}
        <section className="mb-12">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900">
              FSBO Homes in {city.name}
            </h2>
            <Button onClick={() => navigate(`/search?city=${city.name}`)} variant="outline">
              View All <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-4 border-orange-600 border-t-transparent mx-auto"></div>
              <p className="mt-4 text-gray-600">Loading {city.name} listings...</p>
            </div>
          ) : listings.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {listings.slice(0, 6).map((listing) => (
                <Card
                  key={listing.id}
                  className="overflow-hidden hover:shadow-xl transition-shadow cursor-pointer group"
                  onClick={() => navigate(`/listing/${listing.id}`)}
                >
                  <div className="relative h-48 bg-gray-200">
                    {listing.photos?.[0] ? (
                      <img
                        src={listing.photos[0]}
                        alt={`${listing.title} - FSBO home in ${city.name}, TX`}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        loading="lazy"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Home className="w-16 h-16 text-gray-400" />
                      </div>
                    )}
                    <div className="absolute top-3 left-3 bg-orange-600 text-white px-3 py-1 rounded-full text-sm font-bold">
                      FSBO
                    </div>
                  </div>
                  <div className="p-4">
                    <div className="text-2xl font-bold text-gray-900 mb-1">
                      ${listing.price?.toLocaleString()}
                    </div>
                    <p className="text-gray-600 text-sm mb-2 line-clamp-1">{listing.address}</p>
                    <div className="flex items-center text-sm text-gray-500">
                      <span>{listing.bedrooms} bed</span>
                      <span className="mx-2">•</span>
                      <span>{listing.bathrooms} bath</span>
                      <span className="mx-2">•</span>
                      <span>{listing.sqft?.toLocaleString()} sqft</span>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          ) : (
            <Card className="p-8 text-center">
              <Home className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-700 mb-2">No Active Listings</h3>
              <p className="text-gray-500 mb-4">Be the first to list your {city.name} home!</p>
              <Button onClick={() => navigate('/register')} className="bg-orange-600 hover:bg-orange-700">
                List Your Home FREE
              </Button>
            </Card>
          )}
        </section>

        {/* Local Attractions */}
        {city.nearbyAttractions && (
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              <Star className="w-6 h-6 inline mr-2 text-yellow-500" />
              {city.name} Attractions & Amenities
            </h2>
            <div className="flex flex-wrap gap-3">
              {city.nearbyAttractions.map((attraction) => (
                <span key={attraction} className="bg-amber-50 text-amber-700 px-4 py-2 rounded-full text-sm">
                  {attraction}
                </span>
              ))}
            </div>
          </section>
        )}

        {/* FAQ Section */}
        <section className="mb-12 bg-white rounded-xl p-6 md:p-8 shadow-lg">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6">
            Frequently Asked Questions About FSBO in {city.name}
          </h2>
          
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                How do I sell my house by owner in {city.name}, TX?
              </h3>
              <p className="text-gray-600">
                To sell your {city.name} home by owner (FSBO), list it for FREE on Its-Sold.com. Create your listing with photos, set your price, and connect directly with buyers. You&apos;ll save 6% in agent commissions - that&apos;s ${Math.round(parseInt(city.medianPrice.replace(/[$,]/g, '')) * 0.06).toLocaleString()} on the median {city.name} home!
              </p>
            </div>
            
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                What is the median home price in {city.name}?
              </h3>
              <p className="text-gray-600">
                The median home price in {city.name}, TX is approximately {city.medianPrice}. This varies by neighborhood - areas like {city.neighborhoods.slice(0, 2).join(' and ')} may be higher, while other areas offer more affordable options.
              </p>
            </div>
            
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Which school districts serve {city.name}?
              </h3>
              <p className="text-gray-600">
                {city.name} is served by {city.schoolDistricts?.join(', ')}. School district boundaries vary by neighborhood, so verify the specific district for any property you&apos;re considering.
              </p>
            </div>
          </div>
        </section>

        {/* Nearby Cities */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            Explore Nearby DFW Cities
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
            {Object.entries(dfwCities)
              .filter(([slug]) => slug !== citySlug)
              .slice(0, 12)
              .map(([slug, cityInfo]) => (
                <Link
                  key={slug}
                  to={`/homes-for-sale/${slug}`}
                  className="text-center p-3 bg-white rounded-lg shadow hover:shadow-md transition-shadow"
                >
                  <p className="font-medium text-gray-900">{cityInfo.name}</p>
                  <p className="text-xs text-gray-500">{cityInfo.county} County</p>
                </Link>
              ))}
          </div>
        </section>

        {/* Final CTA */}
        <section className="bg-gray-900 text-white rounded-xl p-8 text-center">
          <h2 className="text-3xl font-bold mb-4">
            Ready to Sell Your {city.name} Home?
          </h2>
          <p className="text-gray-300 mb-6 max-w-2xl mx-auto">
            Join thousands of DFW homeowners who have saved on commissions by selling FSBO. List your {city.name} home for FREE today!
          </p>
          <Button 
            onClick={() => navigate('/register')} 
            className="bg-orange-600 hover:bg-orange-700 text-white font-bold text-lg px-8 py-6"
          >
            List My {city.name} Home FREE →
          </Button>
        </section>
      </div>
    </div>
  );
};

export default CityLandingEnhanced;
